import { GSP_MAP } from "@/constants/meterpoints"
import { postcodeFormatter } from "@/helpers/postcodeFormatterHelper"
import {
  IMPANAddress,
  IMPRNAddress,
  IMPXNAddress,
  IMeterpointAddress,
} from "./meterpointAddress.types"

function formatAddress(
  address1 = "",
  address2 = "",
  address3 = "",
  address4 = "",
  address5 = "",
  address6 = "",
  address7 = "",
  address8 = "",
  address9 = "",
  address10 = "",
) {
  const addresses = [
    address1,
    address2,
    address3,
    address4,
    address5,
    address6,
    address7,
    address8,
    address9,
    address10,
  ]

  const formattedAddress = addresses
    .filter((address) => address !== "")
    .join(" ")
    .replace(/(\b\w+\b)(?=.*\b\1\b)/g, "")
    .trim()

  return formattedAddress
}

export class MPXNAddress implements IMPXNAddress {
  postcode: string
  address1?: string
  address2?: string
  address3?: string
  address4?: string
  address5?: string
  address6?: string
  address7?: string
  address8?: string
  address9?: string
  address10?: string
  constructor(
    postcode: string,
    address1?: string,
    address2?: string,
    address3?: string,
    address4?: string,
    address5?: string,
    address6?: string,
    address7?: string,
    address8?: string,
    address9?: string,
    address10?: string,
  ) {
    this.postcode = postcode
    this.address1 = address1
    this.address2 = address2
    this.address3 = address3
    this.address4 = address4
    this.address5 = address5
    this.address6 = address6
    this.address7 = address7
    this.address8 = address8
    this.address9 = address9
    this.address10 = address10
  }

  get formatted() {
    return formatAddress(
      this.address1,
      this.address2,
      this.address3,
      this.address4,
      this.address5,
      this.address6,
      this.address7,
    )
  }

  get county() {
    return formatAddress(this.address8, this.address9, this.address10)
  }
}

function addressWords(formatted: string): string[] {
  return formatted
    .toLowerCase()
    .split(/\s+/)
    .map((word) => word.replace(/[^a-z0-9]/g, ""))
    .filter((word) => word !== "")
}

const MAX_EXTRA_WORDS = 1

function sharesOrderedPrefix(shorter: string[], longer: string[]): boolean {
  return shorter.every((word, index) => longer[index] === word)
}

export function addressesRepresentSameProperty(
  a: IMPXNAddress,
  b: IMPXNAddress,
): boolean {
  if (postcodeFormatter(a.postcode) !== postcodeFormatter(b.postcode)) {
    return false
  }

  const wordsA = addressWords(a.formatted)
  const wordsB = addressWords(b.formatted)

  if (wordsA.length === 0 || wordsB.length === 0) {
    return false
  }

  const [shorter, longer] =
    wordsA.length <= wordsB.length ? [wordsA, wordsB] : [wordsB, wordsA]

  if (longer.length - shorter.length > MAX_EXTRA_WORDS) {
    return false
  }

  return sharesOrderedPrefix(shorter, longer)
}

export function addressesMatchExactly(
  a: IMPXNAddress,
  b: IMPXNAddress,
): boolean {
  return (
    postcodeFormatter(a.postcode) === postcodeFormatter(b.postcode) &&
    addressWords(a.formatted).join(" ") === addressWords(b.formatted).join(" ")
  )
}

export class MPANAddress implements IMPANAddress {
  address: IMPXNAddress
  identifier: string
  profileClassId: number | null
  meterTimeSwitchCode: number | null
  lineLossFactorClassId: string
  gsp: string
  standardSettlementConfigurationId: string
  meterTypeCode: string
  meterSerialNumber: string
  measurementClass: string
  isEnergised: boolean

  constructor(
    address: IMPXNAddress,
    identifier: string,
    profileClassId: number | null,
    meterTimeSwitchCode: number | null,
    lineLossFactorClassId: string,
    gsp: string,
    standardSettlementConfigurationId: string,
    meterTypeCode: string,
    meterSerialNumber: string,
    measurementClass: string,
    isEnergised: boolean,
  ) {
    this.address = address
    this.identifier = identifier
    this.profileClassId = profileClassId
    this.meterTimeSwitchCode = meterTimeSwitchCode
    this.lineLossFactorClassId = lineLossFactorClassId
    this.gsp = gsp
    this.standardSettlementConfigurationId = standardSettlementConfigurationId
    this.meterTypeCode = meterTypeCode
    this.meterSerialNumber = meterSerialNumber
    this.measurementClass = measurementClass
    this.isEnergised = isEnergised
  }

  get isExport(): boolean {
    return this.profileClassId === 8
  }
}

export class MPRNAddress implements IMPRNAddress {
  address: IMPXNAddress
  identifier: string
  designation: string
  meterSerialNumber: string
  isPrepay: boolean

  constructor(
    address: IMPXNAddress,
    identifier: string,
    designation: string,
    meterSerialNumber: string,
    isPrepay: boolean,
  ) {
    this.address = address
    this.identifier = identifier
    this.designation = designation
    this.meterSerialNumber = meterSerialNumber
    this.isPrepay = isPrepay
  }
}

export class MeterpointAddress implements IMeterpointAddress {
  address: IMPXNAddress
  gsp: string
  mpans: IMPANAddress[]
  mprns: IMPRNAddress[]

  constructor(
    address: IMPXNAddress,
    gsp: string,
    mpans: IMPANAddress[],
    mprns: IMPRNAddress[],
  ) {
    this.address = address
    this.gsp = gsp
    this.mpans = mpans
    this.mprns = mprns
  }

  get gspName() {
    return GSP_MAP.get(this.gsp)
  }
}
